import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import JsonLd from './JsonLd'

export interface SEOProps {
  title: string
  description: string
  canonicalUrl?: string
  ogType?: 'website' | 'article' | 'product'
  ogImage?: string
  keywords?: string[]
  author?: string
  publishedTime?: string
  modifiedTime?: string
  price?: number
  currency?: string
  availability?: 'in stock' | 'out of stock'
  noindex?: boolean
  nofollow?: boolean
  isPremium?: boolean
  category?: string
  tags?: string[]
  /** Optional JSON-LD (e.g. WebPage for legal/policy routes). */
  jsonLd?: Record<string, unknown>
}

export default function SEOHead({
  title,
  description,
  canonicalUrl,
  ogType = 'website',
  ogImage = 'https://learn.rondanini.com/og-image.jpg',
  keywords = [],
  author = 'Rondanini Publishing Ltd',
  publishedTime,
  modifiedTime,
  price,
  currency = 'GBP',
  availability = 'in stock',
  noindex = false,
  nofollow = false,
  isPremium = false,
  category,
  tags = [],
  jsonLd
}: SEOProps) {
  const siteUrl = 'https://learn.rondanini.com'
  const path =
    typeof window !== 'undefined' ? window.location.pathname : '/'
  const fullCanonicalUrl = canonicalUrl || `${siteUrl}${path}`

  // For premium content, we allow indexing but mark as premium
  // This helps with discovery while protecting content
  const robotsDirective = noindex || nofollow
    ? `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`
    : 'index, follow'

  // Combine default and custom keywords
  const allKeywords = [
    ...keywords,
    'financial education',
    'professional finance',
    'FX trading',
    'derivatives',
    'risk management',
    category
  ].filter(Boolean).join(', ')

  // Full title with branding (skip suffix when title already carries site or company name)
  const fullTitle =
    title.includes('Rondanini') || title.includes('learn.rondanini.com')
      ? title
      : `${title} | Rondanini Publishing`

  // React 19 hoists <title> but leaves an existing meta description in place, and
  // react-helmet-async does not apply meta or JSON-LD. Keep the head in sync on navigation.
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute('content') ?? null
    if (meta) meta.setAttribute('content', description)

    let canonical = document.querySelector('link[rel="canonical"]')
    const createdCanonical = !canonical
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    const previousCanonical = canonical.getAttribute('href')
    canonical.setAttribute('href', fullCanonicalUrl)

    return () => {
      if (meta && previousDescription != null) meta.setAttribute('content', previousDescription)
      if (createdCanonical) canonical?.remove()
      else if (previousCanonical) canonical?.setAttribute('href', previousCanonical)
    }
  }, [description, fullCanonicalUrl])

  return (
    <>
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsDirective} />
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Rondanini Publishing" />
      <meta property="og:locale" content="en_GB" />

      {/* Article specific tags */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {ogType === 'article' && category && (
        <meta property="article:section" content={category} />
      )}
      {ogType === 'article' && tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Product specific tags (for paid modules) */}
      {price && (
        <>
          <meta property="product:price:amount" content={price.toString()} />
          <meta property="product:price:currency" content={currency} />
          <meta property="product:availability" content={availability} />
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:creator" content="@RondaniniPub" />
      <meta name="twitter:site" content="@RondaniniPub" />

      {/* Additional Meta Tags */}
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />

      {/* Premium content indicator (for internal use and potential crawlers) */}
      {isPremium && (
        <meta name="content:access" content="premium" />
      )}

      {/* Prevent caching of dynamic content */}
      <meta httpEquiv="cache-control" content="no-cache" />
      <meta httpEquiv="expires" content="0" />
      <meta httpEquiv="pragma" content="no-cache" />
    </Helmet>
    {jsonLd ? <JsonLd data={jsonLd} /> : null}
    </>
  )
}
